import React, { type FunctionComponent, type ReactNode, useContext, useEffect, useState } from 'react';
import type { IDisposable, IInitializable } from '../Types';

function canDispose(arg: unknown): arg is IDisposable {
    return (arg as IDisposable).dispose !== undefined;
}

function dispose(arg: unknown) {
    if (canDispose(arg)) {
        arg.dispose();
    }
}

function canInitialize(arg: unknown): arg is IInitializable {
    return (arg as IInitializable).initialize !== undefined;
}

function initialize(arg: unknown) {
    if (canInitialize(arg)) {
        arg.initialize();
    }
}

export function useStore<TStore>(storeFactory: () => () => TStore): TStore {
    const createStore = storeFactory();

    const [store] = useState<TStore>(() => {
        return createStore();
    });

    useEffect(() => {
        initialize(store);

        return () => {
            dispose(store);
        };
    }, [store]);

    return store;
}

export interface ContextComponentProps {
    children: ReactNode;
}

export interface StoreContextItem<TStore> {
    readonly ProviderComponent: FunctionComponent<ContextComponentProps>;
    readonly useStore: () => TStore;
}

export function createStoreContextItem<TStore>(storeFactory: () => () => TStore): StoreContextItem<TStore> {
    const context = React.createContext<TStore | null>(null);

    const ProviderComponent: FunctionComponent<ContextComponentProps> = (props) => {
        const { children } = props;
        const ContextComponent = context;
        const store = useStore<TStore>(storeFactory);

        return <ContextComponent.Provider value={store}>{children}</ContextComponent.Provider>;
    };

    const useStoreWrapper = (): TStore => {
        const store = useContext(context);
        if (store === null) {
            if (import.meta.hot) {
                // Context lost during HMR — force a full page reload to recover
                console.warn('[StoreContext] Store context lost during hot reload — reloading page');
                window.location.reload();
                return undefined as unknown as TStore;
            }
            throw new Error('Store is null');
        }

        return store;
    };

    return {
        ProviderComponent: ProviderComponent,
        useStore: useStoreWrapper,
    };
}
